/**
 * career-bench-lab — standing visibility into every eval/bench run (DEV only, routes
 * `/dev/bench-lab` + `/dev/bench-lab/:label`, gated on `session.devMode`).
 *
 * ONE surface, two modes. No `urn` ⇒ INDEX: a card per results directory (kind,
 * freshness, live badge, transcript count, report link) plus the cost-ledger panel.
 * A `urn:career:bench:<label>` ⇒ DETAIL: kind-aware panels — a diversity round×task
 * cell grid + aggregates, an LLM-panel resolution summary, or an appgen manifest with
 * a link to the grading surface. A urn change REBINDS the VM (KitElement §4), so both
 * index→detail and run→run re-fetch cleanly.
 *
 * The tab polls every ~10 s WHILE VISIBLE (Page Visibility API) so a running bench
 * ticks forward without a manual reload; a "zuletzt aktualisiert" stamp shows the last
 * fetch. Read-only against the dev BFF (`/api/dev/bench-lab/*`). German chrome
 * throughout (dev tool — literal German is acceptable, as on the grading surface).
 *
 * Type-only import from `../../src/bench-lab`: the payload shapes are shared with the
 * BFF; `import type` is erased at build, so the server module's Bun/`node:fs` runtime
 * never reaches the client bundle.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, RouterToken, apiGet } from "../services"
import type { CareerI18n } from "../../i18n"
import type {
  BenchLabIndex,
  CostLedgerSummary,
  DiversityDetail,
  DiversityTask,
  LlmPanelDetail,
  AppgenDetail,
  RunDetail,
  RunIndexEntry,
} from "../../src/bench-lab"

const POLL_MS = 10_000
const URN_PREFIX = "urn:career:bench:"

/** German human labels for the detected kinds (index badge + detail heading). */
const KIND_LABEL: Record<RunDetail["kind"], string> = {
  diversity: "Diversitäts-Bench",
  "llm-panel": "LLM-Panel",
  appgen: "Appgen-Bench",
  unknown: "Unbekannt",
}

// ─── Pure formatting helpers ───────────────────────────────────────────────────────

/** A compact German relative age from an ISO timestamp (or a ms delta). */
export function relAge(fromIsoOrNull: string | null, now = Date.now()): string {
  if (!fromIsoOrNull) return "—"
  const ms = now - Date.parse(fromIsoOrNull)
  if (!Number.isFinite(ms)) return "—"
  return relAgeMs(ms)
}
export function relAgeMs(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return "—"
  const s = Math.max(0, Math.round(ms / 1000))
  if (s < 60) return `vor ${s}s`
  const m = Math.round(s / 60)
  if (m < 60) return `vor ${m} min`
  const h = Math.round(m / 60)
  if (h < 48) return `vor ${h} h`
  return `vor ${Math.round(h / 24)} T`
}
/** USD with 4 decimals (marginal costs are cents-scale). */
export const usd = (n: number | null): string => (n === null ? "—" : `$${n.toFixed(4)}`)
/** A 0–1 fraction as a whole-percent. */
export const pct = (n: number | null): string => (n === null ? "—" : `${Math.round(n * 100)} %`)

// ─── ViewModel ──────────────────────────────────────────────────────────────────────

export class BenchLabVm implements ViewModel {
  readonly index: Signal<BenchLabIndex | null> = signal<BenchLabIndex | null>(null)
  readonly detail: Signal<RunDetail | null> = signal<RunDetail | null>(null)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  /** Client-side timestamp of the last successful fetch ("zuletzt aktualisiert"). */
  readonly updatedAt: Signal<number | null> = signal<number | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  private timer: ReturnType<typeof setInterval> | null = null

  constructor(
    /** The results-dir label when in DETAIL mode; null in INDEX mode. */
    readonly label: string | null,
    private readonly router: Router,
    readonly t: CareerI18n["t"],
  ) {
    this.renderDeps = [this.index, this.detail, this.error, this.loaded, this.updatedAt]
  }

  attach(): () => void {
    void this.refresh()
    // Poll only while the tab is visible — a hidden tab is throttled anyway, and a
    // background bench does not need our attention until the operator looks back.
    const onVisible = (): void => {
      if (typeof document !== "undefined" && !document.hidden) void this.refresh()
    }
    this.timer = setInterval(onVisible, POLL_MS)
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible)
    return () => {
      if (this.timer !== null) clearInterval(this.timer)
      this.timer = null
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible)
    }
  }

  async refresh(): Promise<void> {
    try {
      if (this.label) {
        this.detail.set(await apiGet<RunDetail>(`/api/dev/bench-lab/${encodeURIComponent(this.label)}`))
      } else {
        this.index.set(await apiGet<BenchLabIndex>("/api/dev/bench-lab"))
      }
      this.error.set(null)
      this.updatedAt.set(Date.now())
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

// ─── Element ──────────────────────────────────────────────────────────────────────

export class CareerBenchLab extends KitElement<BenchLabVm> {
  protected createViewModel(ctx: KitContext): BenchLabVm {
    const i18n = ctx.inject(I18nToken)
    const urn = this.urn // urn:career:bench:<label> in detail mode, null in index mode
    const label = urn && urn.startsWith(URN_PREFIX) ? decodeURIComponent(urn.slice(URN_PREFIX.length)) : null
    return new BenchLabVm(label, ctx.inject(RouterToken), i18n.t)
  }

  // ── shared chrome ──────────────────────────────────────────────────────────────

  #stamp(vm: BenchLabVm): TemplateResult {
    const at = vm.updatedAt.value
    return html`<span class="bench-lab-stamp" role="status" aria-live="polite" lang="de"
      >Zuletzt aktualisiert: ${at ? relAgeMs(Date.now() - at) : "—"}</span
    >`
  }

  // ── index ────────────────────────────────────────────────────────────────────────

  #ledgerPanel(ledgers: readonly CostLedgerSummary[]): TemplateResult {
    if (ledgers.length === 0) return html`<p class="bench-lab-empty" lang="de">Keine Kosten-Ledger gefunden.</p>`
    return html`
      <div class="bench-lab-ledgers">
        ${repeat(
          ledgers,
          (l) => l.name,
          (l) => html`
            <article class="bench-lab-ledger">
              <header class="bench-lab-ledger-head">
                <h3 class="bench-lab-ledger-name" lang="de">${l.name}</h3>
                <span class="bench-lab-ledger-sum" lang="de">${usd(l.sumUsd)}</span>
              </header>
              <dl class="bench-lab-ledger-meta">
                <div><dt lang="de">Einträge</dt><dd>${l.entryCount}</dd></div>
                <div><dt lang="de">Gemeldet</dt><dd>${usd(l.sumReportedUsd)}</dd></div>
                <div><dt lang="de">Letzter</dt><dd>${relAgeMs(l.lastEntryAgeMs)}</dd></div>
              </dl>
              ${l.byRound.length > 0
                ? html`<ul class="bench-lab-ledger-rounds" role="list">
                    ${repeat(
                      l.byRound,
                      (r) => r.round,
                      (r) => html`<li lang="de"><span>${r.round}</span><span>${r.entries}×</span><span>${usd(r.sumUsd)}</span></li>`,
                    )}
                  </ul>`
                : nothing}
            </article>
          `,
        )}
      </div>
    `
  }

  #runCard(vm: BenchLabVm, run: RunIndexEntry): TemplateResult {
    const url = `/dev/bench-lab/${encodeURIComponent(run.label)}`
    return html`
      <a
        class=${classMap({ "bench-lab-card": true, [`kind-${run.kind}`]: true, "is-live": run.live })}
        href=${url}
        data-act="nav"
        data-url=${url}
        lang="de"
      >
        <header class="bench-lab-card-head">
          <span class="bench-lab-kind" lang="de">${KIND_LABEL[run.kind]}</span>
          ${run.smoke ? html`<span class="bench-lab-tag bench-lab-tag-smoke" lang="de">Smoke</span>` : nothing}
          ${run.live ? html`<span class="bench-lab-tag bench-lab-tag-live" lang="de">Live</span>` : nothing}
        </header>
        <h3 class="bench-lab-card-title" lang="de">${run.label}</h3>
        <dl class="bench-lab-card-meta">
          <div><dt lang="de">Form</dt><dd lang="de">${run.note}</dd></div>
          <div><dt lang="de">Transkripte</dt><dd>${run.transcriptCount}</dd></div>
          <div><dt lang="de">Aktualität</dt><dd lang="de">${relAge(run.lastModified)}</dd></div>
        </dl>
        ${run.hasReport ? html`<span class="bench-lab-card-report" lang="de">HTML-Report vorhanden</span>` : nothing}
      </a>
    `
  }

  #renderIndex(vm: BenchLabVm): TemplateResult {
    const idx = vm.index.value
    return html`
      <section class="surface bench-lab" aria-labelledby="bench-lab-title">
        <header class="bench-lab-header">
          <h1 id="bench-lab-title" class="surface-title" lang="de">Bench-Labor</h1>
          <div class="bench-lab-header-side">
            ${this.#stamp(vm)}
            <button type="button" class="bench-lab-refresh" data-act="refresh" lang="de">Aktualisieren</button>
          </div>
        </header>
        <p class="surface-lead" lang="de">
          Jeder Eval-/Bench-Lauf unter dem Ergebnis-Verzeichnis — Form automatisch erkannt, Live-Status aus dem
          jüngsten Dateischreiben. Die Ansicht aktualisiert sich alle ${POLL_MS / 1000} s, solange der Tab sichtbar ist.
        </p>
        ${vm.error.value ? html`<p class="surface-error" role="alert" lang="de">Laden fehlgeschlagen: ${vm.error.value}</p>` : nothing}
        ${!idx
          ? vm.loaded.value
            ? html`<p class="surface-empty" lang="de">Keine Läufe gefunden.</p>`
            : html`<p class="bench-lab-loading" aria-busy="true" lang="de">Lade Läufe …</p>`
          : html`
              <h2 class="surface-section-title" lang="de">Läufe (${idx.runs.length})</h2>
              ${idx.runs.length === 0
                ? html`<p class="surface-empty" lang="de">Noch keine Läufe im Ergebnis-Verzeichnis.</p>`
                : html`<div class="bench-lab-grid">
                    ${repeat(idx.runs, (r) => r.label, (r) => this.#runCard(vm, r))}
                  </div>`}
              <h2 class="surface-section-title" lang="de">Kosten-Ledger</h2>
              ${this.#ledgerPanel(idx.ledgers)}
            `}
      </section>
    `
  }

  // ── detail ─────────────────────────────────────────────────────────────────────

  #detailHeader(vm: BenchLabVm, detail: RunDetail): TemplateResult {
    return html`
      <header class="bench-lab-header">
        <div class="bench-lab-detail-head">
          <a class="bench-lab-back" href="/dev/bench-lab" data-act="nav" data-url="/dev/bench-lab" lang="de">← Alle Läufe</a>
          <h1 class="surface-title" lang="de">${detail.label}</h1>
          <span class="bench-lab-kind" lang="de">${KIND_LABEL[detail.kind]}</span>
          ${detail.hasReport
            ? html`<a class="bench-lab-report-link" href=${`/api/dev/bench-lab/${encodeURIComponent(detail.label)}/report`} target="_blank" rel="noopener" lang="de">HTML-Report öffnen ↗</a>`
            : nothing}
        </div>
        <div class="bench-lab-header-side">
          ${this.#stamp(vm)}
          <button type="button" class="bench-lab-refresh" data-act="refresh" lang="de">Aktualisieren</button>
        </div>
      </header>
    `
  }

  #diversityTask(task: DiversityTask): TemplateResult {
    return html`
      <article class="bench-lab-task">
        <header class="bench-lab-task-head">
          <h4 class="bench-lab-task-title" lang="de">${task.taskId}</h4>
          <span class="bench-lab-task-meta" lang="de">${task.okModels ?? "—"}/${(task.okModels ?? 0) + (task.failedModels ?? 0)} ok · ${task.transcriptCount} Transkripte · ${usd(task.costUsd)}</span>
        </header>
        <div class="bench-lab-aggregates">
          <span class="bench-lab-agg" lang="de">Übereinstimmung R1: <strong>${pct(task.agreementRound1)}</strong></span>
          ${task.agreementFinal !== null ? html`<span class="bench-lab-agg" lang="de">final: <strong>${pct(task.agreementFinal)}</strong></span>` : nothing}
          ${task.flipTotal !== null ? html`<span class="bench-lab-agg" lang="de">Umschwünge: <strong>${task.flipSuccesses}/${task.flipTotal}</strong></span>` : nothing}
          ${task.itemCountsByClass
            ? html`<span class="bench-lab-agg" lang="de">Items: ${Object.entries(task.itemCountsByClass).map(([k, v]) => `${k} ${v}`).join(" · ")}</span>`
            : nothing}
        </div>
        <div class="bench-lab-cells">
          ${repeat(
            task.models,
            (m) => m.label,
            (m) => html`<div class="bench-lab-cell-row">
              <span class="bench-lab-cell-model" lang="de">${m.label}</span>
              <span class="bench-lab-cell-states">
                ${repeat(
                  m.cells,
                  (_c, i) => `${m.label}:${i}`,
                  (c) => html`<span
                    class=${classMap({ "bench-lab-state": true, [`state-${c.status}`]: true })}
                    title=${c.condition ? `${c.condition}: ${c.status}` : c.status}
                    lang="de"
                    >${c.condition ?? c.status}</span
                  >`,
                )}
              </span>
            </div>`,
          )}
        </div>
      </article>
    `
  }

  #diversityDetail(vm: BenchLabVm, detail: DiversityDetail): TemplateResult {
    return html`
      <section class="surface bench-lab bench-lab-detail" aria-labelledby="bench-lab-title">
        ${this.#detailHeader(vm, detail)}
        ${detail.smoke ? html`<p class="bench-lab-smoke-note" lang="de">Smoke-Lauf (Schnelltest, reduzierter Umfang).</p>` : nothing}
        ${detail.rounds.length === 0
          ? html`<p class="surface-empty" lang="de">Keine Runden gefunden.</p>`
          : repeat(
              detail.rounds,
              (r) => r.round,
              (r) => html`<div class="bench-lab-round">
                <h3 class="surface-section-title" lang="de">${r.round}</h3>
                ${r.tasks.length === 0
                  ? html`<p class="bench-lab-empty" lang="de">Keine Aufgaben in dieser Runde.</p>`
                  : html`<div class="bench-lab-tasks">${repeat(r.tasks, (t) => t.taskId, (t) => this.#diversityTask(t))}</div>`}
              </div>`,
            )}
      </section>
    `
  }

  #panelDetail(vm: BenchLabVm, detail: LlmPanelDetail): TemplateResult {
    return html`
      <section class="surface bench-lab bench-lab-detail" aria-labelledby="bench-lab-title">
        ${this.#detailHeader(vm, detail)}
        <div class="bench-lab-aggregates">
          <span class="bench-lab-agg" lang="de">Karten: <strong>${detail.cardCount ?? "—"}</strong></span>
          <span class="bench-lab-agg" lang="de">Modelle: <strong>${detail.okModelCount ?? detail.modelCount ?? "—"}</strong></span>
          ${detail.agreeWithAuthorCards !== null
            ? html`<span class="bench-lab-agg" lang="de">Autor-Konsens: <strong>${detail.agreeWithAuthorCards}/${detail.cardCount ?? "—"}</strong> Karten</span>`
            : nothing}
          ${detail.tiedCards !== null ? html`<span class="bench-lab-agg" lang="de">Gleichstände: <strong>${detail.tiedCards}</strong></span>` : nothing}
        </div>
        <h3 class="surface-section-title" lang="de">Modelle</h3>
        ${detail.models.length === 0
          ? html`<p class="surface-empty" lang="de">Keine Modell-Zusammenfassungen.</p>`
          : html`<div class="bench-lab-cells">
              ${repeat(
                detail.models,
                (m) => m.label,
                (m) => html`<div class="bench-lab-cell-row">
                  <span class="bench-lab-cell-model" lang="de">${m.label}</span>
                  <span class="bench-lab-cell-states">
                    <span class=${classMap({ "bench-lab-state": true, [`state-${m.status}`]: true })} lang="de">${m.status}</span>
                    ${m.agreedWithAuthorCount !== null
                      ? html`<span class="bench-lab-cell-note" lang="de">${m.agreedWithAuthorCount}/${m.answeredCount ?? "—"} mit Autor</span>`
                      : nothing}
                  </span>
                </div>`,
              )}
            </div>`}
      </section>
    `
  }

  #appgenDetail(vm: BenchLabVm, detail: AppgenDetail): TemplateResult {
    return html`
      <section class="surface bench-lab bench-lab-detail" aria-labelledby="bench-lab-title">
        ${this.#detailHeader(vm, detail)}
        <dl class="bench-lab-appgen-meta">
          <div><dt lang="de">Datum</dt><dd lang="de">${detail.date ?? "—"}</dd></div>
          <div><dt lang="de">Modelle</dt><dd lang="de">${detail.models.length}</dd></div>
          <div><dt lang="de">Fixtures</dt><dd>${detail.fixtureCount ?? "—"}</dd></div>
          <div><dt lang="de">Generierungen</dt><dd>${detail.generationCount ?? "—"}</dd></div>
          <div><dt lang="de">Strittig</dt><dd>${detail.totalContested ?? "—"}</dd></div>
          <div><dt lang="de">Kosten</dt><dd>${usd(detail.totalCostUsd)}</dd></div>
        </dl>
        ${detail.models.length > 0
          ? html`<p class="bench-lab-appgen-models" lang="de">${detail.models.join(" · ")}</p>`
          : nothing}
        <a class="bench-lab-cta" href=${detail.gradingHref} data-act="nav" data-url=${detail.gradingHref} lang="de">Zur Bench-Bewertung →</a>
      </section>
    `
  }

  #unknownDetail(vm: BenchLabVm, detail: RunDetail & { kind: "unknown" }): TemplateResult {
    return html`
      <section class="surface bench-lab bench-lab-detail" aria-labelledby="bench-lab-title">
        ${this.#detailHeader(vm, detail)}
        <p class="surface-lead" lang="de">
          Form nicht erkannt — der Lauf wird ehrlich als unbekannt ausgewiesen (keine 500). Inhalt:
        </p>
        ${detail.childDirs.length > 0
          ? html`<p class="bench-lab-unknown-line" lang="de"><strong>Verzeichnisse:</strong> ${detail.childDirs.join(" · ")}</p>`
          : nothing}
        ${detail.files.length > 0
          ? html`<p class="bench-lab-unknown-line" lang="de"><strong>Dateien:</strong> ${detail.files.join(" · ")}</p>`
          : html`<p class="surface-empty" lang="de">Leeres Verzeichnis.</p>`}
      </section>
    `
  }

  #renderDetail(vm: BenchLabVm): TemplateResult {
    const detail = vm.detail.value
    if (!detail) {
      return html`
        <section class="surface bench-lab bench-lab-detail" aria-labelledby="bench-lab-title">
          <header class="bench-lab-header">
            <a class="bench-lab-back" href="/dev/bench-lab" data-act="nav" data-url="/dev/bench-lab" lang="de">← Alle Läufe</a>
            <h1 id="bench-lab-title" class="surface-title" lang="de">Lauf</h1>
          </header>
          ${vm.error.value
            ? html`<p class="surface-error" role="alert" lang="de">Laden fehlgeschlagen: ${vm.error.value}</p>`
            : vm.loaded.value
              ? html`<p class="surface-empty" lang="de">Lauf nicht gefunden.</p>`
              : html`<p class="bench-lab-loading" aria-busy="true" lang="de">Lade Lauf …</p>`}
        </section>
      `
    }
    if (detail.kind === "diversity") return this.#diversityDetail(vm, detail)
    if (detail.kind === "llm-panel") return this.#panelDetail(vm, detail)
    if (detail.kind === "appgen") return this.#appgenDetail(vm, detail)
    return this.#unknownDetail(vm, detail)
  }

  protected render(vm: BenchLabVm): TemplateResult {
    return vm.label ? this.#renderDetail(vm) : this.#renderIndex(vm)
  }

  protected override actions(vm: BenchLabVm): ActionMap {
    return {
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
      refresh: () => void vm.refresh(),
    }
  }
}
